import { useRef, useEffect, useState } from 'react'
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  ListBulletIcon,
  Bars3Icon,
  ArrowLongRightIcon,
  ArrowLongLeftIcon,
} from '@heroicons/react/24/outline'
import { ChallengeService } from '@duck/api-definitions'
import { useEventListener } from '@modules/EventBusModule'
import { useTar } from '@/modules/i18n'
import DOMPurify from 'dompurify'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@duck/ui'
import { useIsMobile } from '@duck/ui'
import {
  cleanChartContainersFromElement,
  cleanChartContainersFromHTML,
  getContentChartIds,
  saveSelection,
  restoreSelection,
  insertNodeAtSelection,
  initializeChart,
} from './wysiwygUtils'

interface WysiwygEditorProps {
  onChange?: (content: string) => void
  contentGetter?: () => string
  content?: string
  placeholder?: string
  className?: string
  readOnly?: boolean
  handleChartAdded?: (content: string, chart: ChallengeService.Chart) => void
  handleChartRemoved?: (content: string, remainingChartIds: string[]) => void
  startingCharts?: ChallengeService.WorkState['charts']
}

export const WysiwygEditor = ({
  onChange,
  contentGetter,
  content = '',
  placeholder = 'Start typing...',
  className = '',
  readOnly = false,
  startingCharts = {},
  handleChartAdded,
  handleChartRemoved,
}: WysiwygEditorProps) => {
  const { t } = useTar()
  const isMobile = useIsMobile()
  const editorRef = useRef<HTMLDivElement>(null)
  const loadedChartIds = useRef<string[]>(Object.keys(startingCharts))
  const [formatBlock, setFormatBlock] = useState<string>('p')
  const savedSelectionRef = useRef<Range | null>(null)
  const eventListenerCleanupRef = useRef<(() => void) | void>(undefined)

  // Save selection on user interaction
  useEffect(() => {
    if (readOnly || !editorRef.current) {
      return
    }

    const saveSelectionHandler = () => {
      savedSelectionRef.current = saveSelection(editorRef)
    }

    const editor = editorRef.current
    editor.addEventListener('click', saveSelectionHandler)
    editor.addEventListener('keyup', saveSelectionHandler)
    editor.addEventListener('mouseup', saveSelectionHandler)

    return () => {
      editor.removeEventListener('click', saveSelectionHandler)
      editor.removeEventListener('keyup', saveSelectionHandler)
      editor.removeEventListener('mouseup', saveSelectionHandler)
    }
  }, [readOnly])

  // Content synchronization effect
  useEffect(() => {
    if (editorRef.current) {
      const currentContent = editorRef.current.innerHTML
      const newContent = contentGetter?.() || content || ''

      // Only update if content actually changed to avoid cursor position issues
      if (currentContent !== newContent) {
        editorRef.current.innerHTML = DOMPurify.sanitize(newContent)
      }
    }
  }, [content])

  const rerenderCharts = (): (() => void) | void => {
    if (!editorRef.current) {
      return
    }
    const cleanupFunctions: (() => void)[] = []
    cleanChartContainersFromElement(editorRef.current)
    if (Object.keys(startingCharts).length > 0) {
      Object.entries(startingCharts).forEach(([id, chart]) => {
        const cleanup = initializeChart(id, chart, editorRef, readOnly, updateContent)
        if (cleanup) {
          cleanupFunctions.push(cleanup)
        }
      })
    }
    return () => {
      cleanupFunctions.forEach(cleanup => cleanup?.())
    }
  }

  // Chart initialization on mount
  useEffect(() => {
    return rerenderCharts()
  }, [startingCharts])

  // Cleanup event listener cleanups on unmount or when dependencies change
  useEffect(() => {
    return () => {
      if (eventListenerCleanupRef.current) {
        eventListenerCleanupRef.current()
        eventListenerCleanupRef.current = undefined
      }
    }
  }, [startingCharts, readOnly, handleChartAdded])

  useEventListener('charts:triggerRedraw', () => {
    // Cleanup previous cleanup if it exists
    if (eventListenerCleanupRef.current) {
      eventListenerCleanupRef.current()
    }
    // Store new cleanup function
    eventListenerCleanupRef.current = rerenderCharts()
  }, [startingCharts])

  useEventListener('chart:added', ({ chart }) => {
    if (!chart.id || !editorRef.current) {
      return
    }

    // Restore the saved selection if available
    if (savedSelectionRef.current) {
      restoreSelection(savedSelectionRef.current)
    }

    const chartContainer = document.createElement('div')
    chartContainer.className = `chart-container w-full relative my-2 p-2 border border-border rounded-md overflow-hidden`
    chartContainer.setAttribute('data-chart-id', chart.id)

    // Insert at the current selection position (or saved position)
    insertNodeAtSelection(chartContainer, editorRef)

    const separator = document.createElement('div')
    separator.innerHTML = '&nbsp;'
    insertNodeAtSelection(separator, editorRef)

    if (handleChartAdded) { // will trigger a rerender and hit of the rerenderCharts useEffect
      handleChartAdded(cleanChartContainersFromHTML(editorRef.current?.innerHTML || ''), chart)
      loadedChartIds.current.push(chart.id)
    } else {
      // Cleanup previous cleanup if it exists
      if (eventListenerCleanupRef.current) {
        eventListenerCleanupRef.current()
      }
      // Store new cleanup function
      eventListenerCleanupRef.current = rerenderCharts()
    }
  }, [readOnly, handleChartAdded])

  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value)
    editorRef.current?.focus()
    updateContent()
  }

  const updateContent = () => {
    if (editorRef.current) {
      const remainingChartIds = getContentChartIds(editorRef.current)
      if (remainingChartIds.length < loadedChartIds.current.length) {
        if (handleChartRemoved) {
          handleChartRemoved(cleanChartContainersFromHTML(editorRef.current.innerHTML), remainingChartIds)
        }
        loadedChartIds.current = remainingChartIds
        return
      }
      onChange?.(cleanChartContainersFromHTML(editorRef.current.innerHTML))
    }
  }

  const handleInput = () => {
    updateContent()
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const text = e.clipboardData.getData('text/plain')
    document.execCommand('insertText', false, text)
  }

  const increaseIndent = () => {
    execCommand('indent')
  }

  const decreaseIndent = () => {
    execCommand('outdent')
  }

  const ToolbarButton = ({
    onClick,
    icon: Icon,
    title,
    active = false,
  }: {
    onClick: () => void
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
    title: string
    active?: boolean
  }) => (
    <button
      onClick={onClick}
      title={title}
      className={`p-2 hover:bg-accent/10 rounded-sm transition-colors ${active ? 'bg-accent text-foreground' : 'text-foreground'
        }`}
    >
      <Icon className="w-4 h-4" />
    </button>
  )

  return (
    <div
      className={`p-2 w-full flex flex-col flex-1 overflow-hidden ${className}`}
    >
      {/* Toolbar */}
      {!readOnly && (
        <div className="flex items-center gap-1 px-2 w-full">
          {/* Headers */}
          <Select
            value={formatBlock}
            onValueChange={(value) => {
              setFormatBlock(value)
              if (value === 'p') {
                execCommand('formatBlock', '<p>')
              } else {
                execCommand('formatBlock', `<${value}>`)
              }
            }}
          >
            <SelectTrigger className="w-[140px] h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="p">{t('common.paragraph')}</SelectItem>
              <SelectItem value="h1">{t('common.heading1')}</SelectItem>
              <SelectItem value="h2">{t('common.heading2')}</SelectItem>
              <SelectItem value="h3">{t('common.heading3')}</SelectItem>
              <SelectItem value="h4">{t('common.heading4')}</SelectItem>
            </SelectContent>
          </Select>

          <div className="w-px h-6 bg-border mx-1" />

          {!isMobile && (
            <>
              {/* Text formatting */}
              <ToolbarButton
                onClick={() => execCommand('bold')}
                icon={BoldIcon}
                title={t('common.bold')}
              />
              <ToolbarButton
                onClick={() => execCommand('italic')}
                icon={ItalicIcon}
                title={t('common.italic')}
              />
              <ToolbarButton
                onClick={() => execCommand('underline')}
                icon={UnderlineIcon}
                title={t('common.underline')}
              />

              <div className="w-px h-6 bg-border mx-1" />
            </>
          )}

          {/* Lists */}
          <ToolbarButton
            onClick={() => execCommand('insertUnorderedList')}
            icon={ListBulletIcon}
            title={t('common.unorderedList')}
          />
          <ToolbarButton
            onClick={() => execCommand('insertOrderedList')}
            icon={Bars3Icon}
            title={t('common.orderedList')}
          />

          <div className="w-px h-6 bg-border mx-1" />

          {/* Indentation */}
          <ToolbarButton
            onClick={decreaseIndent}
            icon={ArrowLongLeftIcon}
            title={t('common.decreaseIndent')}
          />
          <ToolbarButton
            onClick={increaseIndent}
            icon={ArrowLongRightIcon}
            title={t('common.increaseIndent')}
          />
        </div>
      )}

      {/* Editor Container */}
      <div className="overflow-auto flex-1 h-full custom-scrollbar">
        <div
          ref={editorRef}
          contentEditable={!readOnly}
          onInput={readOnly ? undefined : handleInput}
          onPaste={readOnly ? undefined : handlePaste}
          className="wysiwyg-editor w-full h-full p-2 text-foreground focus:outline-none [&_span]:!text-foreground"
          style={{
            fontFamily: 'inherit',
            fontSize: '14px',
            lineHeight: '1.5',
            minHeight: '100%',
          }}
          data-placeholder={placeholder}
        />
      </div>
    </div>
  )
} 